import React from 'react';
import {
    Paper, Box, TextField, Typography, Stack, IconButton, Select,
    MenuItem, FormControl, InputLabel, Divider, Button, Switch
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd';
import { Rule, Path, PathCondition, Offer, LandingPage, Campaign } from './CampaignEditorPage';
import PathBuilder from './PathBuilder';

const DIMENSIONS_OPTIONS = [
    'country_code', 'region_name', 'city_name', 'isp', 'device_type', 'os_name', 'browser_name', 'language',
    'sub_id1', 'sub_id2', 'sub_id3', 'sub_id4', 'sub_id5', 'sub_id6', 'sub_id7', 'sub_id8', 'sub_id9', 'sub_id10'
];
const OPERATOR_OPTIONS = ['IS', 'IS_NOT', 'CONTAINS'];

interface RuleBuilderProps {
    ruleData: Rule;
    offers: Offer[];
    landingPages: LandingPage[];
    campaigns: Campaign[];
    onChange: (updatedRule: Rule) => void;
    onDelete: () => void;
    dragHandleProps: DraggableProvidedDragHandleProps | null | undefined;
}

const RuleBuilder: React.FC<RuleBuilderProps> = ({ ruleData, offers, landingPages, campaigns, onChange, onDelete, dragHandleProps }) => {

    const handleFieldChange = (field: keyof Rule, value: any) => {
        onChange({ ...ruleData, [field]: value });
    };

    const handleConditionChange = (index: number, field: keyof PathCondition, value: string) => {
        const newConditions = [...(ruleData.conditions || [])];
        newConditions[index] = { ...newConditions[index], [field]: value };
        handleFieldChange('conditions', newConditions);
    };

    const addCondition = () => {
        const newConditions = [...(ruleData.conditions || []), { dimension: 'country_code', operator: 'IS', value: '' }];
        handleFieldChange('conditions', newConditions);
    };

    const removeCondition = (index: number) => {
        const newConditions = [...(ruleData.conditions || [])];
        newConditions.splice(index, 1);
        handleFieldChange('conditions', newConditions);
    };

    const handlePathChange = (updatedPath: Path, index: number) => {
        const newPaths = [...(ruleData.paths || [])];
        newPaths[index] = updatedPath;
        handleFieldChange('paths', newPaths);
    };

    const addPathToRule = () => {
        const newPath: any = {
            id: Date.now(),
            name: `Nuevo Path en Regla #${(ruleData.paths?.length || 0) + 1}`,
            weight: 100,
            status: 'active',
            items: []
        };
        handleFieldChange('paths', [...(ruleData.paths || []), newPath]);
    };

    const deletePathFromRule = (index: number) => {
        const newPaths = [...(ruleData.paths || [])];
        newPaths.splice(index, 1);
        handleFieldChange('paths', newPaths);
    };

    return (
        <Paper elevation={3} sx={{ p: 2, borderLeft: 5, borderColor: 'secondary.main' }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <IconButton {...dragHandleProps} sx={{ cursor: 'grab' }}><DragIndicatorIcon /></IconButton>
                <TextField
                    label="Nombre de la Regla"
                    value={ruleData.name}
                    onChange={(e) => handleFieldChange('name', e.target.value)}
                    variant="standard"
                    fullWidth
                />
                <Switch defaultChecked />
                <IconButton onClick={onDelete} color="error"><DeleteIcon /></IconButton>
            </Stack>
            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" gutterBottom>SI (Condiciones)</Typography>
            <Stack spacing={1.5} mb={2}>
                {(ruleData.conditions || []).map((condition, index) => (
                    <Stack direction="row" spacing={1} key={index} alignItems="center">
                        <FormControl fullWidth size="small" sx={{flex: 1.5}}>
                            <InputLabel>Dimensión</InputLabel>
                            <Select label="Dimensión" value={condition.dimension} onChange={(e) => handleConditionChange(index, 'dimension', e.target.value)}>
                                {DIMENSIONS_OPTIONS.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small" sx={{flex: 1}}>
                            <InputLabel>Operador</InputLabel>
                            <Select label="Operador" value={condition.operator} onChange={(e) => handleConditionChange(index, 'operator', e.target.value as any)}>
                                {OPERATOR_OPTIONS.map(op => <MenuItem key={op} value={op}>{op}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <TextField label="Valor" value={condition.value} onChange={(e) => handleConditionChange(index, 'value', e.target.value)} sx={{flex: 2}} size="small"/>
                        <IconButton onClick={() => removeCondition(index)} size="small"><DeleteIcon /></IconButton>
                    </Stack>
                ))}
                <Button onClick={addCondition} size="small" startIcon={<AddCircleIcon />}>Añadir Condición</Button>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" gutterBottom>ENTONCES (Paths)</Typography>
            <Box sx={{ pl: 4, mt: 2 }}>
                {(ruleData.paths || []).map((path, index) => (
                    <Box key={path.id} sx={{ mb: 2 }}>
                       <PathBuilder
                            pathData={path}
                            offers={offers}
                            landingPages={landingPages}
                            campaigns={campaigns}
                            onChange={(updatedPath) => handlePathChange(updatedPath, index)}
                            onDelete={() => deletePathFromRule(index)}
                            dragHandleProps={null}
                        />
                    </Box>
                ))}
                <Button variant="outlined" startIcon={<AddCircleIcon />} size="small" onClick={addPathToRule}>Añadir Path a la Regla</Button>
            </Box>
        </Paper>
    );
};

export default RuleBuilder;
